import {
  Component,
  inject,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { AnnouncementsBannerService } from './announcements-banner.service';
import { Subject } from 'rxjs';
import { StorageService } from '../../services/storage/storage.service';
import { SYSTEMCATEGORY } from '../../services/auth/auth.service';

// Shown to a Non-Participating library's own logged-in staff. Which one appears is driven by the
// library's Borrowing/Lending participation flags; a library participating in both scopes sees none.
// Only the scope phrase differs between the three messages.
const notParticipatingMessage = (scope: string): string =>
  `Your library is not ${scope}. Please contact your system administrator with any questions.`;

const LENDING_NOT_PARTICIPATING = notParticipatingMessage('a participating lender');
const BORROWING_NOT_PARTICIPATING = notParticipatingMessage('a participating borrower');
const NOT_PARTICIPATING_IN_ILL = notParticipatingMessage('participating in ILL');

@Component({
  selector: 'ag-announcements-banner',
  templateUrl: './announcements-banner.component.html',
  styleUrl: './announcements-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AnnouncementsBannerComponent implements OnInit, OnDestroy {
  private _bannerService = inject(AnnouncementsBannerService);
  private _router = inject(Router);
  private _storageService = inject(StorageService);
  private _destroyed$ = new Subject<void>();

  private readonly _allowedPaths = ['dashboard', 'request-manager'];

  // CMS announcements are suppressed when the banner is hosted inside a legacy staff iframe.
  @Input() showAnnouncements = true;

  // Set when the banner is hosted on the legacy Participant Record page, which lives inside the
  // iframe (outside the _allowedPaths routes) but must still show the participation notice.
  @Input() isParticipantRecord = false;

  // The legacy iframe hosts every staff page under a single Angular route, so the route-based `show`
  // scope would match them all. The iframe sets this false so its participation notice is gated only
  // by isParticipantRecord (menu item 25) rather than the container route.
  @Input() useRouteScope = true;

  readonly announcements$ = this._bannerService.announcements$;
  show = false;

  // Non-participation notice for the logged-in library's staff, or null when there is nothing to show.
  participationMessage: string | null = null;

  // The notice follows the same routes as announcements, plus the Participant Record page.
  get showParticipation(): boolean {
    return !!this.participationMessage && (this.show || this.isParticipantRecord);
  }

  ngOnInit(): void {
    this._bannerService.load();
    this._setParticipationMessage();
    this._updateShow(this._router.url);
    this._router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this._destroyed$),
      )
      .subscribe((e) => this._updateShow(e.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  private _updateShow(url: string): void {
    this.show = this.useRouteScope && this._allowedPaths.some((p) => url.includes(p));
  }

  private _setParticipationMessage(): void {
    const userData = this._storageService.getUserData();

    // Staff-only notice; patron/guest pages never show it. A missing flag means participating.
    if (!userData?.isStaff) {
      return;
    }

    const isBorrowing = userData.illOptions.isBorrowingEnabled;
    const isLending = userData.illOptions.isLendingEnabled;

    // Only an explicit false shows a notice — the feature is on and this library is not a participant
    // for that scope. null (feature off for the customer) or true (participating) shows nothing.
    if (userData.systemCategory === SYSTEMCATEGORY.SHAREIT) {
      if (isBorrowing === false && isLending === false) {
        this.participationMessage = NOT_PARTICIPATING_IN_ILL;
      } else if (isLending === false) {
        this.participationMessage = LENDING_NOT_PARTICIPATING;
      } else if (isBorrowing === false) {
        this.participationMessage = BORROWING_NOT_PARTICIPATING;
      }
    }
  }
}
